import { defineStore, acceptHMRUpdate } from 'pinia'
import { getFieldPathStatistics } from '@/scripts/task'
import { useTasksStore } from '@/stores/tasks'

export const useTasksFilterStore = defineStore('tasksFilter', {
    state: () => ({
        pathType: 'all',
        pathTypeOptions: [], // only path type, sorted by value alphabetically
        loading: false,
        pathStatistics: null, // null = not loaded yet
        filteredTasks: [],
        filteredDisabledTasks: [],
        status: 'all',
        statusOptions: [
            { name: 'Planned', value: 'planned' },
            { name: 'Running', value: 'running' },
            { name: 'Completed', value: 'completed' },
            { name: 'All', value: 'all' }
        ],
        searchQueryName: '',
        hasApplicationMap: 'all',
        hasApplicationMapOptions: [
            { name: 'Yes', value: true },
            { name: 'No', value: false },
            { name: 'All', value: 'all' }
        ],
        hasISODownload: 'all',
        hasISODownloadOptions: [
            { name: 'Yes', value: true },
            { name: 'No', value: false },
            { name: 'All', value: 'all' }
        ],
        hasPaths: 'all',
        hasPathsOptions: [
            { name: 'Yes', value: 'yes' },
            { name: 'No', value: 'no' },
            { name: 'All', value: 'all' }
        ],
        hasDisabledTasks: 'no',
        hasDisabledTasksOptions: [
            { name: 'Yes', value: 'yes' },
            { name: 'No', value: 'no' }
        ],
        notValidTaskTypes: ['unknown', 'all', 'disabled']
    }),
    getters: {
        getFilteredPlans: (state) => (plans) => {
            if (!plans) return []
            return state.pathType === 'all' 
                ? plans 
                : plans.filter(plan => plan.type === state.pathType)
        }
    },
    actions: {
        setPathType(pathType) {
            this.pathType = pathType
        },
        resetPathType() {
            this.pathType = 'all'
        },
        async getFieldPathStatistic() {
            const tasksStore = useTasksStore()

            try {
                this.loading = true
                this.pathStatistics = null
                this.pathTypeOptions = []

                const fieldId = tasksStore.field.id
                const statistics = await getFieldPathStatistics(fieldId)

                this.pathStatistics = statistics

                // Remove for the moment 'disabled' key from pathStatistics, because don't need it for now
                delete this.pathStatistics.disabled

                // alphabetically sorted path type options
                this.pathTypeOptions = Object.keys(this.pathStatistics).map(key => 
                    ({ value: key })).sort((a, b) => a.value.localeCompare(b.value))
                    
            } catch (error) {
                console.error(error)
                this.pathStatistics = { all: 0 }
            } finally {
                this.loading = false
            }
        },
        matchesTask(task) {
            if (!task) return false

            // 1) name / id search
            if (this.searchQueryName && this.searchQueryName.trim() !== '') {
                const q = this.searchQueryName.toLowerCase()
                const byName = task.name?.toLowerCase().includes(q)
                const byId = task.id?.toString().includes(q)
                if (!byName && !byId) return false
            }

            // 2) path type
            if (this.pathType !== 'all') {
                const plans = Array.isArray(task.plans) ? task.plans : []
                if (!plans.some(plan => plan?.type === this.pathType)) return false
            }

            // 3) status
            if (this.status !== 'all') {
                if (task.status !== this.status) return false
            }
            
            // 4) has application map
            if (this.hasApplicationMap !== 'all') {
                if (task.has_application_map !== this.hasApplicationMap) return false
            }

            // 5) has ISO download
            if (this.hasISODownload !== 'all') {
                if (task.has_download !== this.hasISODownload) return false
            }

            // 6) has paths
            if (this.hasPaths !== 'all') {
                const plans = Array.isArray(task.plans) ? task.plans : []
                const hasPaths = this.getFilteredPlans(plans).length > 0
                if (this.hasPaths === 'yes' && !hasPaths) return false
                if (this.hasPaths === 'no' && hasPaths) return false
            }

            return true
        },
        applyFilters() {
            const ts = useTasksStore()
            // start point: tasks for the active period (if exists), otherwise all sorted
            let base = ts.tasksByPeriod?.[ts.activeTasksPeriod]
            if (!base) {
                base = ts.sortedTasks
            }
            const tasks = Array.isArray(base) ? base : Object.values(base || {})
            this.filteredTasks = tasks.filter(task => this.matchesTask(task))
        },
        hasSomeFilters() {
            return (
                this.searchQueryName && this.searchQueryName.trim() !== '' ||
                this.pathType !== 'all' ||
                this.status !== 'all' ||
                this.hasApplicationMap !== 'all' ||
                this.hasISODownload !== 'all' ||
                this.hasPaths !== 'all'
            )
        },
        reset() {
            this.pathType = 'all'
            this.status = 'all'
            this.searchQueryName = ''
            this.hasApplicationMap = 'all'
            this.hasISODownload = 'all'
            this.hasPaths = 'all'
            this.hasDisabledTasks = 'no'

            this.pathTypeOptions = []
            this.loading = false
            this.pathStatistics = null
            this.filteredTasks = []
            this.filteredDisabledTasks = []
        },
        isTaskTypeValid(taskType) {
            // check if task type is valid
            if (!taskType || this.notValidTaskTypes.includes(taskType)) {
                return false
            }
            // not valid task types are not available in path statistics
            const stats = this.pathStatistics || {}
            for (const key in stats) {
                if (key === taskType) {
                    return true
                }
            }
            return false
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useTasksFilterStore, import.meta.hot))
}
